class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onViewportResize = this.updateMaxHeight.bind(this);
    this.summaryRequestId = 0;
    this.summaryAbortController = null;

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.updateMaxHeight();
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.updateMaxHeight();
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
    window.addEventListener('resize', this.onViewportResize);
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);
    window.removeEventListener('resize', this.onViewportResize);
    this.summaryAbortController?.abort();

    removeTrapFocus(this.activeElement);
  }

  updateMaxHeight() {
    // The wrapper sits in normal flow at the popup's landing position, so its
    // rect stays correct even while the popup itself is mid-transform.
    const anchor = this.notification.closest('.cart-notification-wrapper') || this.notification.parentElement;
    const top = anchor.getBoundingClientRect().top;
    const available = Math.max(window.innerHeight - top - 16, 160);
    this.notification.style.setProperty('--cart-notification-premium-a-max-height', `${Math.round(available)}px`);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.cartItemPrice = typeof parsedState.final_price === 'number' ? parsedState.final_price : null;

    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    this.preparePremiumSummary();

    if (this.header) this.header.reveal();
    this.open();

    // Progressive enhancement: never delay the original Shopify popup or its CTAs.
    this.updatePremiumSummary();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  preparePremiumSummary() {
    this.querySelector('[data-cart-notification-item-price]')?.remove();
    this.querySelector('[data-cart-notification-item-quantity]')?.remove();

    const productName = this.querySelector('.cart-notification-product__name');
    if (productName) {
      const price = document.createElement('p');
      price.className = 'cart-notification-premium-a__item-price';
      price.dataset.cartNotificationItemPrice = '';
      price.hidden = true;
      productName.insertAdjacentElement('afterend', price);

      const quantity = document.createElement('p');
      quantity.className = 'cart-notification-premium-a__item-quantity';
      quantity.dataset.cartNotificationItemQuantity = '';
      quantity.hidden = true;
      price.insertAdjacentElement('afterend', quantity);
    }

    const subtotalRow = this.querySelector('[data-cart-notification-subtotal-row]');
    if (subtotalRow) {
      subtotalRow.hidden = true;
      subtotalRow.setAttribute('aria-busy', 'true');
    }
  }

  async updatePremiumSummary() {
    const itemPrice = this.querySelector('[data-cart-notification-item-price]');
    const itemQuantity = this.querySelector('[data-cart-notification-item-quantity]');
    const subtotal = this.querySelector('[data-cart-notification-subtotal]');
    const subtotalRow = this.querySelector('[data-cart-notification-subtotal-row]');

    if (!subtotal || !subtotalRow) return;

    const requestId = ++this.summaryRequestId;
    this.summaryAbortController?.abort();
    this.summaryAbortController = new AbortController();

    try {
      const cartUrl = window.routes?.cart_url || '/cart';
      const response = await fetch(`${cartUrl}.js`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        signal: this.summaryAbortController.signal,
      });

      if (!response.ok) throw new Error('cart-summary-request-failed');

      const cart = await response.json();
      if (requestId !== this.summaryRequestId) return;

      const currentItem = cart.items?.find((item) => item.key === this.cartItemKey);
      const currentItemPrice = currentItem?.final_price ?? this.cartItemPrice;

      if (itemPrice && typeof currentItemPrice === 'number') {
        itemPrice.textContent = this.formatMoney(currentItemPrice, cart.currency);
        itemPrice.hidden = false;
      }

      // Read-only quantity: only the cart's own line quantity, never parsedState —
      // the same variant may already have been in the cart before this add.
      const currentQuantity = currentItem?.quantity;
      if (itemQuantity) {
        if (Number.isInteger(currentQuantity) && currentQuantity > 0) {
          itemQuantity.textContent = `Antal: ${currentQuantity}`;
          itemQuantity.hidden = false;
        } else {
          itemQuantity.hidden = true;
        }
      }

      if (typeof cart.total_price !== 'number') throw new Error('cart-summary-data-missing');

      subtotal.textContent = this.formatMoney(cart.total_price, cart.currency);
      subtotalRow.hidden = false;
    } catch (error) {
      if (error.name !== 'AbortError') {
        itemPrice && (itemPrice.hidden = true);
        itemQuantity && (itemQuantity.hidden = true);
        subtotalRow.hidden = true;
      }
    } finally {
      if (requestId === this.summaryRequestId) subtotalRow.removeAttribute('aria-busy');
    }
  }

  formatMoney(amountInMinorUnits, currency) {
    const locale = document.documentElement.lang || 'sv-SE';
    const currencyCode = currency || 'SEK';
    const minorUnits = Number(amountInMinorUnits);
    const wholeAmount = minorUnits % 100 === 0;

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: wholeAmount ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(minorUnits / 100);
    } catch (_) {
      return `${(minorUnits / 100).toFixed(2)} ${currencyCode}`;
    }
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
